/**
 * AdminDashboard — Süperadmin "Hesaplar" listesi.
 *
 * Sütunlar: health · firma · sahip · plan · süre · trafik · XML senk ·
 *            head · meta · durum · aksiyon.
 * Filtreler: Deneme / Aktif / Süresi azalan ≤3g / Trafik ≥80% /
 *            XML yok / XML güncel değil / Head kopuk / Meta kopuk (multi).
 * Bulk aksiyon: "Tüm Bağlantıları Test Et" — paralel head+meta checker.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users, Search, ArrowRightCircle, Globe, Mail, Loader2, ShieldCheck,
    Plus, Pause, Zap, CheckCircle2, XCircle, AlertTriangle, Filter,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { PageHeader, Card, Eyebrow } from "@/components/design-system";
import { ADMIN_PATH } from "@/lib/admin-path";
import { PLAN_META, HEALTH_META, relTime, readError } from "./_tenant-helpers";
import CreateTenantDialog from "./CreateTenantDialog";

/* ----------------------------- helpers --------------------------------- */
function HealthDot({ health }) {
    const meta = HEALTH_META[health] || HEALTH_META.yellow;
    return (
        <span
            data-testid={`admin-tenant-health-${health}`}
            title={meta.label}
            className={`inline-block h-2 w-2 rounded-full ring-4 ${meta.dot} ${meta.ring}`}
        />
    );
}

function PlanBadge({ plan }) {
    const meta = PLAN_META[plan] || PLAN_META.trial;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${meta.tone}`}
            data-testid="admin-tenant-plan-badge"
        >
            <span className={`h-1 w-1 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
}

function LicenseCountdown({ summary }) {
    if (!summary) return <span className="text-xs text-muted-foreground">—</span>;
    const { status, days_remaining, is_expired } = summary;
    if (is_expired) {
        return <span className="text-[11px] font-medium text-rose-600" data-testid="admin-license-expired">Süresi bitti</span>;
    }
    if (days_remaining == null) return <span className="text-xs text-muted-foreground">—</span>;
    const tone = days_remaining <= 3 ? "text-rose-600" : days_remaining <= 14 ? "text-amber-600" : "text-foreground";
    return (
        <span className={`text-[11px] font-medium ${tone}`} data-testid="admin-license-days">
            {days_remaining} gün{" "}
            <span className="text-muted-foreground font-normal">
                {status === "trial" ? "deneme" : "kaldı"}
            </span>
        </span>
    );
}

function UsageBar({ pct = 0 }) {
    const clamped = Math.min(100, Math.max(0, pct));
    const tone =
        clamped >= 100 ? "[&>div]:bg-rose-500" :
        clamped >= 80  ? "[&>div]:bg-amber-500" :
                         "[&>div]:bg-emerald-500";
    return (
        <div className="min-w-[90px] max-w-[140px]">
            <Progress value={clamped} className={`h-1.5 ${tone}`} data-testid="admin-usage-bar" />
            <div className="text-[10px] text-muted-foreground mt-1">
                %{clamped.toFixed(1)}
            </div>
        </div>
    );
}

function XmlCell({ summary }) {
    if (!summary || !summary.connected) {
        return (
            <span className="text-[11px] text-muted-foreground italic" data-testid="admin-xml-cell">
                Bağlı değil
            </span>
        );
    }
    const { last_sync_at, last_sync_status, hours_since_sync } = summary;
    const never = !last_sync_at;
    const stale = !never && hours_since_sync != null && hours_since_sync > 48;
    const failed = last_sync_status === "failed";
    const tone =
        failed || never ? "text-rose-600" :
        stale           ? "text-amber-600" :
                          "text-foreground";
    const subLabel =
        never  ? "bekliyor" :
        failed ? "başarısız" :
        stale  ? "güncel değil" :
                 "sync ok";
    return (
        <div className="flex flex-col leading-tight" data-testid="admin-xml-cell">
            <span className={`text-[11px] font-medium ${tone}`}>
                {never ? "Hiç çalışmadı" : relTime(last_sync_at)}
            </span>
            <span className="text-[10px] text-muted-foreground">{subLabel}</span>
        </div>
    );
}

/** Head veya Meta kurulum durumu — küçük renkli dot + ikon. */
function InstallDot({ status, label, testId }) {
    const map = {
        connected:    { color: "text-emerald-600", dot: "bg-emerald-500", icon: CheckCircle2,   t: "Bağlı" },
        disconnected: { color: "text-rose-600",    dot: "bg-rose-500",    icon: XCircle,        t: "Kopuk" },
        unknown:      { color: "text-muted-foreground", dot: "bg-muted-foreground/40", icon: AlertTriangle, t: "Test edilmedi" },
    };
    const m = map[status] || map.unknown;
    const Icon = m.icon;
    return (
        <div className="inline-flex items-center gap-1" title={`${label}: ${m.t}`} data-testid={testId}>
            <Icon className={`h-3 w-3 ${m.color}`} strokeWidth={2} />
            <span className={`text-[10px] font-medium ${m.color}`}>{m.t}</span>
        </div>
    );
}

function StatusChip({ status, widget_disabled }) {
    if (widget_disabled) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200" data-testid="admin-tenant-status">
                <Pause className="h-2.5 w-2.5" strokeWidth={2.5} />
                Widget kapalı
            </span>
        );
    }
    const live = status === "active";
    return (
        <span
            className={[
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
                live
                    ? "bg-foreground/10 text-foreground border border-border"
                    : "bg-muted text-muted-foreground border border-border",
            ].join(" ")}
            data-testid="admin-tenant-status"
        >
            <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-muted-foreground/50"}`} />
            {live ? "Aktif" : "Askıda"}
        </span>
    );
}

function TenantRow({ t, onImpersonate, onOpen, busy }) {
    const owner = t?.owner;
    const hasOwner = Boolean(owner?.email);
    const stop = (e) => { e.stopPropagation(); };
    return (
        <tr
            onClick={() => onOpen(t.id)}
            className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
            data-testid={`admin-tenant-row-${t.id}`}
        >
            <td className="py-3 pl-5 pr-2 w-[28px]">
                <HealthDot health={t.health || "yellow"} />
            </td>
            <td className="py-3 px-3">
                <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-[13px] font-medium truncate">
                        {t.company_name || <span className="text-muted-foreground">—</span>}
                    </span>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5 min-w-0">
                        <Globe className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{t.site_domain || "—"}</span>
                    </div>
                </div>
            </td>
            <td className="py-3 px-3">
                {hasOwner ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                        <Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{owner.email}</span>
                    </div>
                ) : (
                    <span className="text-[11px] text-muted-foreground/70 italic">sahipsiz</span>
                )}
            </td>
            <td className="py-3 px-3">
                <PlanBadge plan={
                    t.plan === "sayz"
                        ? (t.license_summary?.billing_cycle || "monthly")
                        : (t.plan || t.license_summary?.status || "trial")
                } />
            </td>
            <td className="py-3 px-3">
                <LicenseCountdown summary={t.license_summary} />
            </td>
            <td className="py-3 px-3">
                <UsageBar pct={t.usage_pct || 0} />
            </td>
            <td className="py-3 px-3">
                <XmlCell summary={t.xml_summary} />
            </td>
            <td className="py-3 px-3">
                <InstallDot status={t.install_summary?.head_status} label="Head" testId={`admin-head-${t.id}`} />
            </td>
            <td className="py-3 px-3">
                <InstallDot status={t.install_summary?.meta_status} label="SKU Etiketi" testId={`admin-meta-${t.id}`} />
            </td>
            <td className="py-3 px-3">
                <StatusChip status={t.status} widget_disabled={t.widget_disabled} />
            </td>
            <td className="py-3 pr-5 pl-3 text-right" onClick={stop}>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={!hasOwner || busy}
                    onClick={() => onImpersonate(t)}
                    className="h-8 text-[11.5px] font-medium"
                    data-testid={`admin-impersonate-btn-${t.id}`}
                    title={hasOwner ? "Bu hesabın paneline bir saatlik oturumla geç" : "Sahip tanımlı değil"}
                >
                    {busy ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                        <ArrowRightCircle className="h-3.5 w-3.5 mr-1" strokeWidth={1.75} />
                    )}
                    Panele Geç
                </Button>
            </td>
        </tr>
    );
}

function HealthLegend() {
    return (
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground" data-testid="admin-health-legend">
            <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Sağlıklı
            </span>
            <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Dikkat
            </span>
            <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Kritik
            </span>
        </div>
    );
}

/** Filtre tanımları — her biri bir tenant row'u filter fonksiyonuyla eşler. */
const FILTERS = [
    { key: "trial",      label: "Deneme",             test: (t) => t.license_summary?.status === "trial" },
    { key: "active",     label: "Aktif",              test: (t) => t.license_summary?.status === "active" },
    { key: "expiring",   label: "Süresi azalan ≤3g",  test: (t) => !t.license_summary?.is_expired && (t.license_summary?.days_remaining ?? 999) <= 3 },
    { key: "high_usage", label: "Trafik ≥%80",         test: (t) => (t.usage_pct || 0) >= 80 },
    { key: "no_xml",     label: "XML yok",             test: (t) => !t.xml_summary?.connected },
    { key: "stale_xml",  label: "XML güncel değil",   test: (t) => t.xml_summary?.connected && (!t.xml_summary?.last_sync_at || (t.xml_summary?.hours_since_sync ?? 0) > 48 || t.xml_summary?.last_sync_status === "failed") },
    { key: "head_bad",   label: "Head kopuk",          test: (t) => t.install_summary?.head_status === "disconnected" },
    { key: "meta_bad",   label: "SKU etiketi kopuk",  test: (t) => t.install_summary?.meta_status === "disconnected" },
];

/** Çoklu seçim filtre chip'leri — OR mantığıyla birleştirir. */
function FilterChips({ active, onToggle, onClear }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-filter-chips">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                <Filter className="h-3 w-3" strokeWidth={1.75} />
                Filtre
            </span>
            {FILTERS.map((f) => {
                const on = active.has(f.key);
                return (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => onToggle(f.key)}
                        data-testid={`admin-filter-${f.key}`}
                        className={[
                            "h-7 px-2 rounded-md border text-[11px] font-medium transition-colors",
                            on
                                ? "bg-foreground text-background border-foreground"
                                : "bg-background text-muted-foreground border-border hover:border-foreground/40",
                        ].join(" ")}
                    >
                        {f.label}
                    </button>
                );
            })}
            {active.size > 0 && (
                <button
                    type="button"
                    onClick={onClear}
                    className="h-7 px-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="admin-filter-clear"
                >
                    Temizle
                </button>
            )}
        </div>
    );
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tenants, setTenants] = useState(null);
    const [query, setQuery] = useState("");
    const [impersonatingId, setImpersonatingId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState(() => new Set());
    const [bulkTesting, setBulkTesting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await adminApi.get("/admin/tenants");
            setTenants(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(readError(err));
            setTenants([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openDetail = useCallback((id) => {
        nav(`${ADMIN_PATH}/tenants/${id}`);
    }, [nav]);

    const impersonate = useCallback(async (t) => {
        if (!t?.id || impersonatingId) return;
        setImpersonatingId(t.id);
        try {
            const { data } = await adminApi.post(`/admin/impersonate/${t.id}`);
            toast.success(`${data?.tenant?.company_name || t.company_name} paneline geçildi`);
            window.location.assign("/app");
        } catch (err) {
            toast.error(readError(err));
            setImpersonatingId(null);
        }
    }, [impersonatingId]);

    const toggleFilter = useCallback((key) => {
        setActiveFilters((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const clearFilters = useCallback(() => setActiveFilters(new Set()), []);

    const bulkTest = useCallback(async () => {
        if (bulkTesting) return;
        setBulkTesting(true);
        const tid = toast.loading("Tüm bağlantılar test ediliyor…");
        try {
            const { data } = await adminApi.post("/admin/tenants/test-all-connections");
            const s = data?.summary || {};
            toast.success(
                `Test tamamlandı · Head ${s.head_connected}/${s.total} bağlı · SKU Etiketi ${s.meta_connected}/${s.total} bağlı`,
                { id: tid }
            );
            await load();
        } catch (err) {
            toast.error(readError(err), { id: tid });
        } finally {
            setBulkTesting(false);
        }
    }, [bulkTesting, load]);

    const { withOwner, withoutOwnerCount, filtered } = useMemo(() => {
        const list = tenants || [];
        const withOwner = list.filter((t) => t.owner?.email);
        const withoutOwnerCount = list.length - withOwner.length;
        const q = query.trim().toLowerCase();
        const activeDefs = FILTERS.filter((f) => activeFilters.has(f.key));
        const matchesFilters = (t) =>
            activeDefs.length === 0 ? true : activeDefs.some((f) => f.test(t));
        const matchesQuery = (t) =>
            !q
                ? true
                : [t.company_name, t.site_domain, t.id, t.owner?.email, t.owner?.name]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q));
        const filtered = withOwner.filter((t) => matchesFilters(t) && matchesQuery(t));
        return { withOwner, withoutOwnerCount, filtered };
    }, [tenants, query, activeFilters]);

    const loading = tenants === null;

    return (
        <div className="space-y-6" data-testid="admin-dashboard-page">
            <PageHeader
                icon={Users}
                title="Hesaplar"
                description="Merchant hesaplarını izle, planlarını yönet, paneline tek tıkla geç."
                actions={
                    <>
                        <Button
                            variant="outline"
                            onClick={bulkTest}
                            disabled={bulkTesting || loading}
                            className="h-9 text-[12px]"
                            data-testid="admin-bulk-test-btn"
                            title="Tüm merchant'ların head ve SKU etiketi kurulum durumunu paralel olarak test eder."
                        >
                            {bulkTesting ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Zap className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />
                            )}
                            Tüm Bağlantıları Test Et
                        </Button>
                        <Button
                            onClick={() => setCreateOpen(true)}
                            className="h-9 bg-foreground text-background hover:bg-foreground/90"
                            data-testid="admin-new-tenant-btn"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
                            Yeni Merchant
                        </Button>
                    </>
                }
            />

            <Card className="p-4" data-testid="admin-dashboard-toolbar">
                <div className="space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Firma, alan adı veya e-posta ara…"
                                className="h-10 pl-9 text-sm"
                                data-testid="admin-tenants-search"
                            />
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                            <HealthLegend />
                            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
                                <span data-testid="admin-tenants-count">
                                    {loading ? "Yükleniyor…" : (
                                        <>
                                            <b className="text-foreground">{filtered.length}</b>
                                            {" / "}{withOwner.length}
                                            {withoutOwnerCount > 0 && (
                                                <span className="text-muted-foreground ml-2">
                                                    (+{withoutOwnerCount} sahipsiz gizlendi)
                                                </span>
                                            )}
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    <FilterChips active={activeFilters} onToggle={toggleFilter} onClear={clearFilters} />
                </div>
            </Card>

            <Card data-testid="admin-tenants-card">
                <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
                    <Eyebrow>Merchant Hesapları</Eyebrow>
                </div>

                {loading ? (
                    <div className="p-10 flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="admin-tenants-loading">
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                        Hesaplar yükleniyor…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="admin-tenants-empty">
                        {query || activeFilters.size > 0 ? "Filtreye uyan hesap bulunamadı." : "Henüz merchant hesabı yok."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-tenants-table">
                            <thead>
                                <tr className="text-left">
                                    <Th />
                                    <Th>Firma</Th>
                                    <Th>Sahip</Th>
                                    <Th>Paket</Th>
                                    <Th>Süre</Th>
                                    <Th>Trafik (Ay)</Th>
                                    <Th>XML Senk.</Th>
                                    <Th>Head</Th>
                                    <Th>Meta</Th>
                                    <Th>Durum</Th>
                                    <Th align="right">Aksiyon</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((t) => (
                                    <TenantRow
                                        key={t.id}
                                        t={t}
                                        onImpersonate={impersonate}
                                        onOpen={openDetail}
                                        busy={impersonatingId === t.id}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <CreateTenantDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={() => load()}
            />
        </div>
    );
}

function Th({ children, align = "left" }) {
    return (
        <th className={`py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${align === "right" ? "text-right pr-5" : ""} ${align === "left" ? "first:pl-5" : ""}`}>
            {children}
        </th>
    );
}
